import * as React from 'react'

import {
  Box,
  Icon,
  List,
  SearchField,
  Spinner,
  Table,
  Text,
} from '@factorialco/deprecated-design-system'
import { orderBy } from 'lodash'

import Popover from 'modules/core/components/PopoverMenu'
import TextField from 'modules/core/components/TextField'
import createComputed from 'modules/core/hooks/createComputed'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { Controls, InfiniteLoader } from 'modules/core/hooks/usePaginatedQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import { UNASSIGNED } from 'modules/finance/hooks/useEmployeeDimensionsAssignments'

import Option, { ValueOption } from './Option'

type SortDirection = 'asc' | 'desc'

interface Props {
  value?: ValueOption
  placeholder: string
  options: ValueOption[]
  controls?: Controls
  withSelectStyle?: boolean
  withUnassignedPlaceholder?: boolean
  onSearchChange?: (search: string) => void
  onChange: (value: ValueOption) => void
  loading?: boolean
  readonly?: boolean
}

const useComputedOptions = createComputed(
  (options, search, order: SortDirection): ValueOption[] => {
    const optionsToDisplay = options.filter((option: ValueOption) => {
      return matchText(search, option.name)
    })
    return orderBy(optionsToDisplay, (option) => option.name, [order])
  }
)

const DimensionValuePicker = ({
  value,
  placeholder,
  options,
  controls,
  withSelectStyle = false,
  withUnassignedPlaceholder = true,
  onSearchChange,
  onChange,
  loading,
  readonly,
}: Props) => {
  const { search, onSearch, debouncedSearch } = useDebouncedSearch()

  const [open, setOpen] = React.useState(false)
  const [direction, setDirection] = React.useState<SortDirection>('asc')
  const onClickSort = () => {
    direction === 'asc' ? setDirection('desc') : setDirection('asc')
  }

  const handleValueChange = (newValue: ValueOption) => {
    setOpen(false)
    onChange(newValue)
  }

  const renderComputedOptions = (listOptions: ValueOption[]) =>
    listOptions.map((option: ValueOption) => (
      <Option key={option.id} option={option} onSelect={handleValueChange} />
    ))

  const computedOptions = useComputedOptions(options, search, direction).get()

  if (computedOptions.length && withUnassignedPlaceholder)
    computedOptions.unshift({
      name: `-- ${i18n.t('finance.employee_dimensions_info.not_assigned')} --`,
      id: UNASSIGNED,
    })

  useUnsafeEffect(() => {
    if (onSearchChange) onSearchChange(debouncedSearch)
  }, [debouncedSearch, onSearchChange])

  const unselectedValue = Number(value?.id) < 0

  return (
    <Box flexGrow>
      <Popover type="dialog" open={open} onClick={() => setOpen(!open)}>
        <Popover.Trigger disabled={options.length < 1 || readonly}>
          {withSelectStyle ? (
            <TextField.Select
              value={value?.name || placeholder}
              readonly={options.length < 1 || readonly}
            />
          ) : (
            <Box>
              <Box
                paddingX="s16"
                flexDirection="row"
                alignItems="center"
                justifyContent="spaceBetween"
              >
                <Text
                  weight="normal"
                  size="300"
                  ellipsis
                  color={unselectedValue ? 'grey600' : 'textPrimary'}
                >
                  {unselectedValue ? placeholder : value?.name}
                </Text>
                <Box>
                  <Icon.Small icon="caret-down" />
                </Box>
              </Box>
            </Box>
          )}
        </Popover.Trigger>
        <Popover.Content side="top" align="center" width="s400">
          <Box padding="s32" gap="s16">
            <List>
              <Box width="full" flexDirection="row" background="grey200" padding="s8">
                <Box flexGrow>
                  <SearchField
                    variant="big"
                    search={search}
                    onSearch={onSearch}
                    placeholder={i18n.t('common.search')}
                  />
                </Box>
              </Box>
              <Box
                width="full"
                alignItems="center"
                background="grey200"
                flexDirection="row"
                paddingX="s16"
                paddingY="s12"
              >
                <Table.SortingHeader onClick={onClickSort} selected={direction === 'asc'}>
                  {i18n.t('common.name')}
                </Table.SortingHeader>
              </Box>

              {loading ? (
                <Box
                  gap="s8"
                  paddingY="s8"
                  width="full"
                  height="s96"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Spinner />
                </Box>
              ) : (
                <>
                  {controls && computedOptions.length > 0 && (
                    <Box maxHeight="s240" overflowY="auto" data-scroll-container>
                      <InfiniteLoader controls={controls}>
                        {renderComputedOptions(computedOptions)}
                      </InfiniteLoader>
                    </Box>
                  )}

                  {!controls && renderComputedOptions(computedOptions)}
                  {!computedOptions.length && (
                    <Box alignItems="center" padding="s40" width="full">
                      <Text.Normal>
                        {i18n.t(
                          'finance.employee_cost_centers_info.modals.set_cost_centers.not_found'
                        )}
                      </Text.Normal>
                    </Box>
                  )}
                </>
              )}
            </List>
          </Box>
        </Popover.Content>
      </Popover>
    </Box>
  )
}

export default DimensionValuePicker
